import type { ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { StatusBar } from "./StatusBar";
import { CREATOR_STATUS_DOT, fmtNum, humanize, pct } from "./format";
import type { RawCreatorStatus } from "./types";

export function Hint({ text, children }: { text: ReactNode; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="cursor-help underline decoration-muted-foreground/40 decoration-dotted underline-offset-[3px] outline-none">
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p className="max-w-64 text-balance">{text}</p>
      </TooltipContent>
    </Tooltip>
  );
}

/** One labelled number; a button when it filters something. */
export function Metric({
  label,
  value,
  dot,
  onClick,
  className,
  children,
}: {
  label: ReactNode;
  value: ReactNode;
  dot?: string;
  onClick?: () => void;
  className?: string;
  children?: ReactNode;
}) {
  const body = (
    <>
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {dot && <span className={cn("size-2 shrink-0 rounded-full", dot)} />}
        {label}
      </span>
      <span className="font-mono text-base font-medium tabular-nums">{value}</span>
      {children}
    </>
  );
  // The button's own padding is pulled back so its text lines up with the plain cells.
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={cn("-mx-1.5 flex min-w-0 cursor-pointer flex-col items-start gap-0.5 rounded-md px-1.5 py-1 text-left outline-none transition-colors hover:bg-accent focus-visible:bg-accent", className)}
    >
      {body}
    </button>
  ) : (
    <div className={cn("flex min-w-0 flex-col gap-0.5 py-1", className)}>{body}</div>
  );
}

/**
 * The analysis funnel of a batch (or of every batch): the stacked bar, then
 * one number per status. Sized by its own width, so it never scrolls sideways.
 */
export function FunnelSummary({
  counts,
  total,
  onFilter,
  extra,
}: {
  counts: Record<RawCreatorStatus, number>;
  total: number;
  /** Status counts become buttons (filter the creators list). */
  onFilter?: (status: RawCreatorStatus) => void;
  /** Cells appended after "Accept rate". */
  extra?: ReactNode;
}) {
  const processed = counts.ACCEPTED + counts.REJECTED + counts.ERRORED;
  const rate = pct(counts.ACCEPTED, counts.ACCEPTED + counts.REJECTED);
  const statuses: RawCreatorStatus[] = ["ACCEPTED", "REJECTED", "ERRORED", "PENDING"];
  if (counts.DUPLICATE) statuses.push("DUPLICATE");

  return (
    <div className="@container flex flex-col gap-2.5">
      {/* Narrow: the bar takes the full width and the count sits under it. */}
      <div className="flex flex-col gap-1 @lg:flex-row @lg:items-center @lg:gap-3">
        <StatusBar counts={counts} total={total} className="@lg:flex-1" />
        <span className="shrink-0 self-end font-mono text-xs tabular-nums text-muted-foreground @lg:self-auto">
          {fmtNum(processed)} of {fmtNum(total)} analysed ({pct(processed, total) ?? 0}%)
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 @md:grid-cols-4 @3xl:grid-flow-col @3xl:grid-cols-none @3xl:auto-cols-fr">
        <Metric label="Creators" value={fmtNum(total)} />
        {statuses.map((status) => (
          <Metric
            key={status}
            label={humanize(status.toLowerCase())}
            dot={CREATOR_STATUS_DOT[status]}
            value={fmtNum(counts[status])}
            onClick={onFilter ? () => onFilter(status) : undefined}
          />
        ))}
        <Metric label={<Hint text="Accepted ÷ (accepted + rejected)">Accept rate</Hint>} value={rate == null ? "—" : `${rate}%`} />
        {extra}
      </div>
    </div>
  );
}
